import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Policies = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "returns-policy",
        "refund-policy",
        "warranty-policy",
        "guarantee-policy",
        "automatic-policy-generation",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Store Policies | xPage Help Center"
        description="Learn how to manage your store policies in Xpage, including returns, refunds, warranties, guarantees, and automatic policy generation. Build trust and streamline operations with customizable policy settings."
        keywords="xPage store policies, returns policy, refund policy, warranty policy, guarantee policy, automatic policy generation, ecommerce policies, store compliance"
        canonical="https://help.xpage.ai/help/settings/Policies"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Policies
          </h1>
          <p className="text-sm text-systemLabel">
            In Xpage, you can easily configure your store's policies to build
            trust with customers and streamline operations. You have the
            flexibility to use default settings or customize everything, and you
            can even generate your policy content automatically based on your
            choices.
          </p>
        </header>

        {/* Returns Policy Section */}
        <section id="returns-policy" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Returns Policy
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Set clear rules for returns:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Return Window
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Choose how long customers have to return products: 14
                      days, 30 days (default), 90 days, Unlimited, or enter a
                      Custom number of days.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Return Shipping Cost
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Decide who pays for return shipping: Customer pays, Free
                      return shipping (default), or Flat rate (you can specify
                      the amount).
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Restocking Fee
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Enable or disable restocking fees, and set a percentage if
                      you want to charge one.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Return Rules
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Apply your return rules only after activation.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Refund Policy Section */}
        <section id="refund-policy" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Refund Policy
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Control how and when customers get their money back:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Refund Method
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Refund to the original payment method (default and only
                      option).
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Refund Timing
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Choose when refunds are processed: Instant, 3–5 business
                      days (default), or 7–10 business days.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Warranty Policy Section */}
        <section id="warranty-policy" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Warranty Policy
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Protect your products and give customers confidence:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Warranty Period
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      1 year (default), 2 years, or Lifetime.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Warranty Coverage
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Choose what your warranty covers: Defects and breakdowns
                      (default), Limited warranty, or Extended coverage.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Guarantee Policy Section */}
        <section id="guarantee-policy" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Guarantee Policy
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Show customers you stand behind your products:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Guarantee Type
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      30-day satisfaction guarantee (default), 60-day, or
                      Lifetime.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Guarantee Terms
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Decide whether to offer a full refund or a replacement.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Automatic Policy Generation Section */}
        <section id="automatic-policy-generation" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Automatic Policy Generation
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Automatic Policy
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Let Xpage create policy content automatically based on
                      your selections.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Manual Update
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Turn off automatic generation to edit the content
                      yourself.
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
                <p className="text-sm text-systemLabel">
                  TIP: While Xpage can automatically generate policy content,
                  it's important to ensure compliance with all applicable laws
                  and regulations when creating your store policies. Always
                  review each policy carefully before publishing.
                </p>
              </div>

              <p className="text-sm text-systemLabel mt-4">
                Once your policies are added, links will automatically appear in
                the footer of your checkout pages so customers can easily access
                them before checkout.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#returns-policy"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "returns-policy"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Returns Policy
              </a>
            </li>
            <li>
              <a
                href="#refund-policy"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "refund-policy"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Refund Policy
              </a>
            </li>
            <li>
              <a
                href="#warranty-policy"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "warranty-policy"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Warranty Policy
              </a>
            </li>
            <li>
              <a
                href="#guarantee-policy"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "guarantee-policy"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Guarantee Policy
              </a>
            </li>
            <li>
              <a
                href="#automatic-policy-generation"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "automatic-policy-generation"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Automatic Policy Generation
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Policies;
